import { UsersThree } from '@phosphor-icons/react';
import type { AssociationDuServeur, CategorieDAssociation } from '@sirocco/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { associations as associationsApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { EmptyState } from '../../components/ui/Bits';
import { SearchField } from '../../components/ui/Field';
import { useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { t } from '../../i18n';
import { sansAccents } from './commun';

/**
 * Les associations d'une ecole, par famille, dans l'ordre ou elle les range.
 *
 * Chacune n'est plus que son nom, pose sur sa banniere : la description,
 * le nombre de membres et ma place vivent sur sa page, a un toucher. Cent
 * vingt clubs se parcourent par famille, comme sur la page de l'ecole, et
 * se reconnaissent a l'oeil, par leurs logos.
 */
export function AssociationsScreen() {
  const { server } = useSession();
  const { data, loading, error, reload, refresh } = useAsync(
    () => associationsApi.list(),
    [server?.id],
    `associations-${server?.id ?? ''}`,
  );
  const [recherche, setRecherche] = useState('');
  const textes = t.messages.annuaire.associations;
  const cherche = sansAccents(recherche.trim());
  const trouvees = (data ?? []).filter(
    (asso) => !cherche || sansAccents(`${asso.nom} ${asso.description}`).includes(cherche),
  );

  return (
    <SubScreen title={textes.titre} onRefresh={refresh} roue>
      {loading && !data && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {data && data.length === 0 && <EmptyState icon={<UsersThree size={38} />} title={textes.vide} />}
      {data && data.length > 0 && (
        <div className="as-ecran">
          <div className="an__recherche">
            <SearchField
              tone="spot"
              className="search--croix-du-fil"
              placeholder={textes.rechercher}
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
              onClear={() => setRecherche('')}
            />
          </div>
          {trouvees.length === 0 && <p className="ms-notice ms-notice--recherche">{textes.aucune(recherche.trim())}</p>}
          {parFamille(trouvees).map(([famille, liste]) => (
            <section key={famille ?? 'autres'} className="as-famille">
              {famille && <h2 className="ms-section as-famille__titre">{textes.categories[famille]}</h2>}
              <div className="as-liste">
                {liste.map((asso) => (
                  <Carte key={asso.id} asso={asso} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </SubScreen>
  );
}

/**
 * Les associations groupees par famille, dans l'ordre du catalogue.
 *
 * Le catalogue arrive deja range famille par famille : on ne fait que le
 * couper la ou la famille change. Une association sans famille ferme la
 * marche, sans titre.
 */
function parFamille(liste: AssociationDuServeur[]): [CategorieDAssociation | null, AssociationDuServeur[]][] {
  const groupes: [CategorieDAssociation | null, AssociationDuServeur[]][] = [];
  for (const asso of liste) {
    const dernier = groupes[groupes.length - 1];
    if (dernier && dernier[0] === asso.categorie) dernier[1].push(asso);
    else groupes.push([asso.categorie, [asso]]);
  }
  return groupes;
}

/** Ma place dans l'association, dite par une pastille : rien si je n'y suis pas. */
export function MaPlace({ asso }: { asso: AssociationDuServeur }) {
  const textes = t.messages.annuaire.associations;
  if (asso.monRole === 'chef') return <span className="as-place as-place--chef">{textes.chef}</span>;
  if (asso.monRole === 'membre') return <span className="as-place">{textes.membre}</span>;
  if (asso.maDemande) return <span className="as-place as-place--attente">{textes.enAttente}</span>;
  return null;
}

/**
 * Une image en fond, sans laisser une adresse sortir de son `url()`.
 *
 * L'adresse vient du catalogue, que seul l'outil ecrit ; on la met quand
 * meme entre guillemets echappes.
 */
export function fondImage(adresse: string): React.CSSProperties {
  return { backgroundImage: `url(${JSON.stringify(adresse)})` };
}

/**
 * La carte : le nom de l'association, pose sur sa banniere.
 *
 * La banniere porte le logo en son milieu, fondu dans ses couleurs. Le nom
 * prend l'encre qui s'y voit — noire sur une banniere claire, blanche sur
 * une foncee —, mesuree par l'outil la ou le nom commence, et un halo de
 * l'encre contraire le garde lisible quand il passe sur le logo. Sans
 * banniere, la carte reste celle du spot, avec le nom seul.
 */
function Carte({ asso }: { asso: AssociationDuServeur }) {
  const textes = t.messages.annuaire.associations;
  const classes = ['card', 'as-carte'];
  if (asso.banniere) classes.push('as-carte--banniere');
  if (asso.encre === 'sombre') classes.push('as-carte--encre-sombre');
  return (
    <Link to={`/app/annuaire/associations/${asso.id}`} className={classes.join(' ')} aria-label={textes.voir(asso.nom)}>
      {/* Une image et non un fond : elle ne se charge qu'a l'approche de l'ecran. */}
      {asso.banniere && (
        <img className="as-carte__banniere" src={asso.banniere} alt="" loading="lazy" decoding="async" />
      )}
      {/*
        Le nom garde son propre sens de lecture — en arabe, « 180 Degrees
        Consulting » devenait « Degrees 180 Consulting » — mais s'aligne sur
        celui de la page : `<bdi>` isole l'un sans toucher a l'autre.
      */}
      <span className="as-carte__nom">
        <bdi>{asso.nom}</bdi>
      </span>
    </Link>
  );
}
